import { PartFinderReport } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { StatusBadge } from '@/components/StatusBadge';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { ReactNode } from 'react';
import { SectionHeading } from '@/components/PartFinder/SectionHeading';
import { EditableDescription } from './EditableDescription';

const Panel = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '6px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  padding: '12px',
  overflowX: 'hidden',
  overflowY: 'auto',
  height: '100%',
  minHeight: 0,
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    {children}
  </Box>
);

export const DetailsPanel = ({ report }: { report: PartFinderReport }) => {
  const { selfAwareEmail } = useGetCurrentUser();
  const searchedNsn = report.nsnReport?.searchedNsn;
  const partNumber = searchedNsn && report.nsnReport ? report.nsnReport.nsn : report.partNumber;

  return (
    <Panel>
      <SectionHeading icon={<InfoOutlined />} title='Details' />
      <Field label='Part Number'>
        <BodyText>{partNumber}</BodyText>
      </Field>
      {searchedNsn && (
        <Field label='Searched'>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BodyText color='text.secondary'>{searchedNsn} (discontinued)</BodyText>
            <Tooltip
              title={`This NSN is discontinued — we automatically expanded the search to its replacement, ${partNumber}.`}
            >
              <InfoOutlined sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            </Tooltip>
          </Box>
        </Field>
      )}
      <Field label='Description'>
        <EditableDescription report={report} />
      </Field>
      <Field label='Status'>
        <StatusBadge status={report.status} progress={report.progress} />
      </Field>
      <Field label='Created'>
        <BodyText color='text.secondary'>{format(new Date(report.createdAt), 'MMM d, yyyy')}</BodyText>
      </Field>
      {report.creator?.email && (
        <Field label='Created By'>
          <BodyText>{selfAwareEmail(report.creator.email)}</BodyText>
        </Field>
      )}
    </Panel>
  );
};
